import { useState, useEffect, useCallback, useRef } from 'react';
import { heroSlides } from '@/mocks/homeData';

interface HeroSectionProps {
  onBookCleanup: () => void;
}

export default function HeroSection({ onBookCleanup }: HeroSectionProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goToSlide = useCallback((index: number) => {
    if (isTransitioning || index === activeIndex) return;
    setIsTransitioning(true);
    setActiveIndex(index);
    setTimeout(() => setIsTransitioning(false), 700);
  }, [activeIndex, isTransitioning]);

  const nextSlide = useCallback(() => {
    const next = (activeIndex + 1) % heroSlides.length;
    goToSlide(next);
  }, [activeIndex, goToSlide]);

  const prevSlide = useCallback(() => {
    const prev = (activeIndex - 1 + heroSlides.length) % heroSlides.length;
    goToSlide(prev);
  }, [activeIndex, goToSlide]);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const current = heroSlides[activeIndex];

  return (
    <section id="home" className="relative w-full min-h-[100dvh] flex items-center overflow-hidden">
      {/* Background slides */}
      {heroSlides.map((slide, i) => (
        <div
          key={slide.id}
          className="absolute inset-0 transition-opacity duration-700 ease-in-out"
          style={{ opacity: i === activeIndex ? 1 : 0 }}
        >
          <img
            src={slide.image}
            alt={slide.label}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
      ))}

      {/* Flat dark overlay */}
      <div className="absolute inset-0 bg-primary-950/60" />

      {/* Content */}
      <div className="relative z-10 container-content w-full pt-24 md:pt-28">
        <div className="max-w-3xl">
          {/* Animated label */}
          <div className="overflow-hidden mb-4">
            <p
              key={`label-${activeIndex}`}
              className="font-label font-medium text-label-lg text-secondary-400 tracking-wide uppercase animate-slide-up"
            >
              {current.label}
            </p>
          </div>

          {/* Animated headline */}
          <div className="overflow-hidden mb-6">
            <h1
              key={`headline-${activeIndex}`}
              className="font-heading text-display-xl text-background-50 text-balance animate-slide-up"
              style={{ animationDelay: '100ms' }}
            >
              {current.headline}
            </h1>
          </div>

          {/* Animated body */}
          <div className="overflow-hidden mb-6">
            <p
              key={`body-${activeIndex}`}
              className="text-body-lg text-background-200 max-w-xl leading-relaxed animate-slide-up"
              style={{ animationDelay: '200ms' }}
            >
              {current.body}
            </p>
          </div>

          {/* Feature tags */}
          <div className="overflow-hidden mb-8">
            <div
              key={`features-${activeIndex}`}
              className="flex flex-wrap gap-2 animate-slide-up"
              style={{ animationDelay: '300ms' }}
            >
              {current.features.map((feature) => (
                <span
                  key={feature}
                  className="px-3 py-1 rounded-full bg-background-50/15 text-background-100 text-body-xs font-medium backdrop-blur-sm border border-background-50/20"
                >
                  {feature}
                </span>
              ))}
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={onBookCleanup}
              className="w-full sm:w-auto bg-primary-500 text-background-50 hover:bg-primary-600 active:bg-primary-700 px-8 py-3.5 rounded-md text-body-md font-label font-semibold transition-colors duration-200 whitespace-nowrap text-center"
            >
              Book a Cleanup
            </button>
            <a
              href="#services"
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById('services');
                if (el) {
                  const top = el.getBoundingClientRect().top + window.scrollY - 80;
                  window.scrollTo({ top, behavior: 'smooth' });
                }
              }}
              className="w-full sm:w-auto px-8 py-3.5 rounded-md text-body-md font-label font-semibold border border-background-300/60 text-background-100 hover:bg-background-50/10 transition-colors duration-200 whitespace-nowrap text-center"
            >
              Explore Services
            </a>
          </div>
        </div>

        {/* Slide indicators */}
        <div className="flex items-center gap-3 mt-12 md:mt-16">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => goToSlide(i)}
              className="group flex items-center gap-2"
              aria-label={`Go to slide ${i + 1}`}
            >
              <span
                className="block h-1 rounded-full transition-all duration-500"
                style={{
                  width: i === activeIndex ? 40 : 16,
                  backgroundColor: i === activeIndex ? 'oklch(var(--primary-500))' : 'rgba(255,255,255,0.4)',
                }}
              />
            </button>
          ))}
        </div>
      </div>

      {/* Side arrows */}
      <button
        onClick={prevSlide}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-50 transition-colors duration-200"
        aria-label="Previous slide"
      >
        <i className="ri-arrow-left-line text-xl" />
      </button>
      <button
        onClick={nextSlide}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 z-20 w-12 h-12 items-center justify-center rounded-full bg-background-50/10 hover:bg-background-50/20 text-background-50 transition-colors duration-200"
        aria-label="Next slide"
      >
        <i className="ri-arrow-right-line text-xl" />
      </button>

      {/* Slide counter */}
      <div className="absolute bottom-8 right-8 z-20 hidden md:flex items-center gap-2 text-background-50/80 font-label text-sm">
        <span className="font-semibold text-background-50">
          {String(activeIndex + 1).padStart(2, '0')}
        </span>
        <span className="w-6 h-px bg-background-50/40" />
        <span>{String(heroSlides.length).padStart(2, '0')}</span>
      </div>
    </section>
  );
}